import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const KEY = "cookie_consent_v1";

function apply(analytics, marketing) {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
    });
  }
  if (typeof window.fbq === "function") {
    window.fbq("consent", marketing ? "grant" : "revoke");
    if (marketing) window.fbq("track", "PageView");
  }
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [decided, setDecided] = useState(true);
  const [prefs, setPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(KEY)); } catch { saved = null; }
    if (saved && typeof saved.analytics === "boolean") {
      setDecided(true);
      setOpen(false);
      setAnalytics(saved.analytics);
      setMarketing(saved.marketing === true);
      apply(saved.analytics, saved.marketing === true);
    } else {
      setDecided(false);
      setOpen(true);
    }
  }, []);

  const save = (a, m) => {
    try { localStorage.setItem(KEY, JSON.stringify({ analytics: a, marketing: m, ts: Date.now() })); } catch { /* ignore */ }
    apply(a, m);
    setDecided(true);
    setOpen(false);
    setPrefs(false);
  };

  const reopen = () => { setPrefs(false); setOpen(true); };

  const location = useLocation();
  // Non mostrare il banner nel pannello admin (evita sovrapposizioni ai modali)
  if (location.pathname.startsWith("/admin")) return null;

  if (!open) {
    // Small persistent control to change consent later
    if (!decided) return null;
    return (
      <button data-testid="cookie-reopen" onClick={reopen} aria-label="Preferenze cookie"
        className="fixed bottom-4 left-4 z-[60] w-11 h-11 rounded-full bg-fir text-ivory shadow-lg flex items-center justify-center hover:bg-fir-deep transition-colors">
        <Cookie size={20} className="text-gold" />
      </button>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-5" data-testid="cookie-banner">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-stone p-5 sm:p-6">
        {decided && (
          <button onClick={() => setOpen(false)} aria-label="Chiudi" className="absolute right-6 top-6 text-fir-deep/40 hover:text-fir-deep">
            <X size={18} />
          </button>
        )}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-fir/10 flex items-center justify-center mt-0.5">
            <Cookie size={20} className="text-gold" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl text-fir mb-1">Rispettiamo la tua privacy</h2>
            <p className="text-fir-deep/75 text-sm leading-relaxed">
              Usiamo cookie tecnici necessari al funzionamento del sito e, con il tuo consenso, cookie statistici
              (Google Analytics) e di marketing (Meta/Facebook Pixel) per misurare l'utilizzo del sito e l'efficacia
              delle campagne pubblicitarie. Puoi accettare, rifiutare o scegliere le preferenze. I cookie tecnici sono sempre attivi.
            </p>
            <p className="text-fir-deep/60 text-xs mt-1.5">
              Maggiori dettagli nella <Link to="/cookie-policy" className="underline hover:text-gold">Cookie Policy</Link> e nell'<Link to="/privacy" className="underline hover:text-gold">Informativa Privacy</Link>.
            </p>

            {prefs && (
              <div className="mt-4 space-y-3" data-testid="cookie-prefs">
                <label className="flex items-center justify-between bg-stone/40 rounded-xl px-4 py-3">
                  <span className="text-sm text-fir font-medium">Cookie tecnici <span className="text-fir-deep/50 font-normal">(sempre attivi)</span></span>
                  <input type="checkbox" checked disabled className="accent-gold w-4 h-4" />
                </label>
                <label className="flex items-center justify-between bg-stone/40 rounded-xl px-4 py-3 cursor-pointer">
                  <span className="text-sm text-fir font-medium">Cookie statistici <span className="text-fir-deep/50 font-normal">(Google Analytics)</span></span>
                  <input data-testid="cookie-analytics-toggle" type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="accent-gold w-4 h-4" />
                </label>
                <label className="flex items-center justify-between bg-stone/40 rounded-xl px-4 py-3 cursor-pointer">
                  <span className="text-sm text-fir font-medium">Cookie di marketing <span className="text-fir-deep/50 font-normal">(Meta/Facebook Pixel)</span></span>
                  <input data-testid="cookie-marketing-toggle" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="accent-gold w-4 h-4" />
                </label>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              {!prefs ? (
                <>
                  <button data-testid="cookie-accept-all" onClick={() => save(true, true)} className="gold-btn px-5 py-2.5 rounded-xl text-sm font-medium">Accetta tutti</button>
                  <button data-testid="cookie-reject" onClick={() => save(false, false)} className="px-5 py-2.5 rounded-xl border border-fir/30 text-fir hover:bg-fir/5 text-sm transition-colors">Rifiuta</button>
                  <button data-testid="cookie-customize" onClick={() => setPrefs(true)} className="px-5 py-2.5 rounded-xl text-fir-deep/70 hover:text-fir text-sm transition-colors">Personalizza</button>
                </>
              ) : (
                <>
                  <button data-testid="cookie-save-prefs" onClick={() => save(analytics, marketing)} className="gold-btn px-5 py-2.5 rounded-xl text-sm font-medium">Salva preferenze</button>
                  <button onClick={() => save(true, true)} className="px-5 py-2.5 rounded-xl border border-fir/30 text-fir hover:bg-fir/5 text-sm transition-colors">Accetta tutti</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
